import { GraphContext } from '@contexts/graphs'
import { Tabs } from '@ui/atoms/tabs'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import React, { useCallback, useContext, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'

import { type DataType } from '../graph-types'
import { mergeOptions } from '../mapper'

ChartJS.register(ArcElement, Tooltip, Legend)
const GsmGraphGraphColors = ['#2AB2AF', '#087F94', '#005E6E']

const externalTooltipHandler = (context: any) => {
  const { chart, tooltip } = context
  const point = tooltip?.dataPoints?.[0]?.raw ?? {}
  const pointPos = tooltip?.dataPoints?.[0]?.dataIndex ?? {}
  const pointId = `id${pointPos}`
  let tooltipEl = chart.canvas.parentNode.querySelector(`#${pointId}`)

  const width = 85
  const height = 35

  const { x, y } = tooltip._active[0]?.element || {}

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.setAttribute('id', pointId)
    tooltipEl.classList.add('chart-gsm-data-label')
    const colors = tooltip.labelColors[0]
    const span = document.createElement('span')
    span.style.background = colors.backgroundColor
    span.style.borderColor = colors.borderColor
    span.setAttribute('id', `span${pointId}`)
    tooltipEl.appendChild(span)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }

  const isNegValue = parseFloat(point) < 0
  tooltipEl.style.left = `${x - width / 2 - 5}px`
  tooltipEl.style.top = `${isNegValue ? y + 35 - height : y - 10 - height}px`
  const tooltipSpanEl = chart.canvas.parentNode.querySelector(`#span${pointId}`)
  const pointValue = parseFloat(point)
  tooltipSpanEl.textContent = `${pointValue >= 0 ? '+' : '-'} ${Math.abs(pointValue)}%`
  tooltipSpanEl.style.color = `white`
  tooltipSpanEl.style.borderRadius = '6px'
  tooltipSpanEl.style.padding = '6px'
  tooltipSpanEl.style.width = `${width}px`
  tooltipSpanEl.style.height = `${height}px`
  tooltipSpanEl.style.textAlign = 'center'
  tooltipSpanEl.style.alignContent = 'center'
  tooltipSpanEl.style.fontWeight = 'bold'
}

type GsmGraphPropsType = {
  options?: Record<string, unknown>
  data: DataType
  handleChange?: (n: number) => void
}

export const GsmGraph: React.FC<GsmGraphPropsType> = ({
  data,
  options,
  handleChange = () => {
    console.log('missing handlechange function')
  },
}) => {
  const { selectedGraph, setSelectedGraph } = useContext(GraphContext)

  useEffect(() => {
    setSelectedGraph(0)
  }, [data])

  const max = Math.max(...data.datasets[selectedGraph]?.data)
  const handleOptionChange = useCallback((value: string) => {
    const newValue = parseInt(value)
    setSelectedGraph(newValue)
    handleChange(newValue)
  }, [])

  const d: DataType = JSON.parse(JSON.stringify(data))
  d.labels.forEach((_l, i) => {
    d.datasets.forEach((_d, j) => {
      d.datasets[j].backgroundColor[i] =
        d.datasets[j].backgroundColor[i] || GsmGraphGraphColors[i]
      d.datasets[j].borderRadius = 50
      d.datasets[j].barThickness = 20
    })
  })
  const onChartReady = (e: any) => {
    e.chart.config._config.data.datasets.map(({ data }, datasetIndex: number) =>
      data.map((_d: any, index: number) =>
        e.chart.tooltip.setActiveElements([
          {
            datasetIndex,
            index,
          },
        ])
      )
    )
  }

  const overideOptions = {
    ...options,
    animation: {
      onComplete: onChartReady,
    },
    responsive: true,
    maintainAspectRatio: true, // change to get small/tall graph
    plugins: {
      ...options.plugins,
      legend: { ...options.plugins.legend, position: 'top', display: false },
      tooltip: {
        enabled: false,
        position: 'average',
        animation: false,
        external: externalTooltipHandler,
      },
      scales: {
        yAxis: [
          {
            gridLines: {
              display: false,
            },
          },
        ],
      },
    },
    scales: {
      y: {
        max: max > 0 ? Math.ceil(max) + 5 : 0,
        ticks: { display: false, stepSize: 20 },
      },
      x: {
        ticks: {
          callback: (_value: number, index: number) =>
            d.labels[index].split(' '),
        },
      },
    },
  }
  const { dateStart, dateEnd } = d.datasets[selectedGraph]
  const hasDates = dateStart && dateEnd

  const formatDate = (date: string) => {
    const d = new Date(date)
    const day = d.getDate()
    const month = d.getMonth() + 1
    return `${day < 10 ? '0' : ''}${day}.${month < 10 ? '0' : ''}${month}.${d.getFullYear()}`
  }

  return (
    <div className="flex w-full flex-col">
      <div className="relative w-full">
        <Bar
          data={{ ...d, datasets: [d.datasets[selectedGraph]] }}
          options={mergeOptions(options, overideOptions)}
          redraw={false}
          updateMode="none"
        />
      </div>
      <Tabs
        className="my-[20px] w-fit max-w-full self-center"
        selectedItem={selectedGraph.toString()}
        items={d.datasets.map((li, index) => ({
          label: li.label,
          value: index.toString(),
        }))}
        onChange={handleOptionChange}
      />
      {hasDates && (
        <div className="pt-10">
          <span className="font-medium">
            Performances {data.title.toLowerCase()} des formules du mandat pour
            un investissement du {formatDate(dateStart)} au{' '}
            {formatDate(dateEnd)},{' '}
          </span>
          nettes des frais annuels de gestion du contrat et des frais liés à la
          gestion sous mandat, et hors prélèvements sociaux et fiscaux.
        </div>
      )}
    </div>
  )
}
